import { autoinject, transient } from "aurelia-dependency-injection";
import { BaseEntity } from "../../modules/base-entity";
import { OnboardingConfigModel, OnboardingModelMeta, OnboardingVerificationPluginModel, OnboardingLandingPage } from "../../../../models/onboarding/onboarding.models";
import { I18N } from "aurelia-i18n";
import { Router } from "aurelia-router";
import { BindingEngine } from "aurelia-binding";
import { EventAggregator, Subscription } from "aurelia-event-aggregator";
import { NotificationClientService } from "../../../../services/basics/notification-client.service";
import { UnitOfWorkItem } from "../../../../utilities/unit-of-work-observer";
import { BaseValidationResponse } from "../../../../models/validations/validations.models";
import { StepModel } from "../../../../models/basics/basics.models";
import { EntityTypes } from "@rightship-tech/common-ui/models/basic.models";
import { OnboardingBasicMeta } from "./steps/oboarding-basic-meta/basic-meta";
import { OnboardingOfferings } from "./steps/onboarding-offerings/onboarding-offerings";
import { OnboardingAdvancedMeta } from "./steps/onboarding-advanced-meta/advanced-meta";
import { Reflection } from "../../../../utilities/reflection";
import { OfferingsSelectionConfigsManagement } from "./steps/offerings-criteria-config/offerings-criteria-config";
import { OnboardingAdminService } from "../../../../services/onboarding/onboarding-admin.service";
import { OnboardingVerification } from "./steps/onboarding-verification/onboarding-verification";
import { OnboardingTermsAndConditions } from "./steps/onboarding-terms-and-conditions/terms-and-conditions";
import { GenesisRouterConstants } from "../../../../configuration/router.constants";
import { Container } from 'aurelia-dependency-injection';
import { OnboardingPluginConfig } from "./steps/onboarding-plugin-config/onboarding-plugin-config";
import { ValidationsClientService } from "../../../../services/validations/validations-client.service";
import { BaseClaims } from "../../../../constants/authentication/base-claims";
import { OnboardingRulesScript } from "./steps/onboarding-rules-script/onboarding-rules-script";
import { UserClaimsService } from "../../../../services/authentication/user-claims.service";
import { ConfigService } from '@rightship-tech/common-ui/services/basic/config.service';

@autoinject()
@transient()
export class Onboarding extends BaseEntity<OnboardingConfigModel> {

    genericPluginName: string = 'generic-plugin';

    modelMeta: OnboardingModelMeta;
    offeringMeta: OnboardingModelMeta;
    landingPages: OnboardingLandingPage[];
    sections: string[];
    verificationPlugins: OnboardingVerificationPluginModel[];
    containerInstance: Container;
    subscriptions: Subscription[] = [];
    offeringConfigMetaPath: string;
    pluginConfigMetaPath: string;
    pluginName: string;

    public get hasUpdatePermission(): boolean {
        return this.userClaimsService.hasPermission(BaseClaims.UpdateClientForms);
    }

    constructor(
        private onboardingBasicMeta: OnboardingBasicMeta,
        private onboardingAdvancedMeta: OnboardingAdvancedMeta,
        private onboardingOffering: OnboardingOfferings,
        private onboardingPluginConfig: OnboardingPluginConfig,
        private onboardingVerification: OnboardingVerification,
        private onboardingTermsAndConditions: OnboardingTermsAndConditions,
        private offeringsSelectionConfigsManagement: OfferingsSelectionConfigsManagement,
        private onboardingRulesScript: OnboardingRulesScript,
        private onboardingAdminService: OnboardingAdminService,
        private userClaimsService: UserClaimsService,
        private configService: ConfigService,
        i18N: I18N,
        router: Router,
        bindingEngine: BindingEngine,
        eventAggregator: EventAggregator,
        notificationService: NotificationClientService,
        validationsClientService: ValidationsClientService) {
        super(router, bindingEngine, i18N, notificationService, eventAggregator, validationsClientService);
        this.containerInstance = Container.instance;
    }

    protected getPropertiesToSkip() {
        return ["queries", "workflowConfiguration", "metaInstance"];
    }

    protected arrayChangesInterceptor(arrayName: string, items: any[], added: boolean) {
        if (arrayName == Reflection.getPropertyName<OnboardingConfigModel>(x => x.offerings)) {
            if (added) {
                return [];
            }
        }
        return items;
    }

    protected getSteps(): StepModel[] {

        return [{
            title: this.i18N.tr('plugin-config-text'),
            step: 0,
            show: true,
            vm: this.onboardingPluginConfig,
            getModel: () => ({ item: this.item, landingPages: this.landingPages, pluginConfigMetaPath: this.pluginConfigMetaPath, variables: this.modelMeta ? this.modelMeta.properties : [] }),
            lazyLoad: true
        },
        {
            title: this.i18N.tr('basic-meta-text'),
            step: 1,
            show: () => this.item.onboardingConfigId > 0,
            vm: this.onboardingBasicMeta,
            getModel: () => ({ item: this.item, variables: this.modelMeta ? this.modelMeta.properties : [], sections: this.sections, pluginName: this.pluginName }),
            lazyLoad: true,
            class: "no-scrolling"
        },
        {
            title: this.i18N.tr('offerings-text'),
            step: 2,
            show: () => this.item.onboardingConfigId > 0,
            vm: this.onboardingOffering,
            getModel: () => ({
                item: this.item,
                variables: this.offeringMeta.properties,
                variablesForIfConditions: this.offeringMeta.properties.concat(this.modelMeta ? this.modelMeta.properties : []),
                offeringConfigMetaPath: this.offeringConfigMetaPath
            }),
            lazyLoad: true
        },
        {
            title: this.i18N.tr('additional-meta-text'),
            step: 3,
            show: () => this.item.onboardingConfigId > 0,
            vm: this.onboardingAdvancedMeta,
            getModel: () => ({ item: this.item, variables: this.modelMeta.properties, sections: this.sections }),
            lazyLoad: true
        },
        {
            title: this.i18N.tr('offerings-selection-config-text'),
            step: 4,
            show: () => this.item.onboardingConfigId > 0,
            vm: this.offeringsSelectionConfigsManagement,
            getModel: () => ({ item: this.item, basicMetaIdentifier: this.modelMeta.entityIdentifier, offeringConfigMetaPath: this.offeringConfigMetaPath }),
            lazyLoad: true
        }, {
            title: this.i18N.tr('verification-config-text'),
            step: 5,
            show: () => this.item.onboardingConfigId > 0,
            vm: this.onboardingVerification,
            getModel: () => ({ item: this.item, verificationPlugins: this.verificationPlugins }),
            lazyLoad: true
        }, {
            title: this.i18N.tr('onboarding-terms-and-conditions-text'),
            step: 6,
            show: () => this.item.onboardingConfigId > 0,
            vm: this.onboardingTermsAndConditions,
            getModel: () => ({ item: this.item }),
            lazyLoad: true
        }, {
            title: this.i18N.tr('onboarding-rules-script-text'),
            step: 7,
            show: () => this.item.onboardingConfigId > 0,
            vm: this.onboardingRulesScript,
            getModel: () => ({ item: this.item, variables: this.modelMeta ? this.modelMeta.properties : [] }),
            lazyLoad: true
        }];
    }

    protected getListRoute(): string {
        return GenesisRouterConstants.RouteNames.Onboardings;
    }

    protected getItemId(): number {
        return this.item ? this.item.onboardingConfigId : null;
    }

    protected getItem(id: number): Promise<OnboardingConfigModel> {
        return new Promise<OnboardingConfigModel>((resolve) => {
            if (this.subscriptions) {
                this.subscriptions.forEach(x => x.dispose());
                this.subscriptions = [];
            }

            this.onboardingAdminService.getOnboardingConfig(id).then(response => {

                let pluginName = this.genericPluginName;
                if (response?.onboardingConfig?.onboardingPlugin?.internalName && !response?.onboardingConfig?.onboardingPlugin?.isDynamic) {
                    pluginName = response.onboardingConfig.onboardingPlugin.internalName;
                }
                const plugin = Object.keys(this.configService?.config?.onboardingRequestPlugins)?.indexOf(pluginName) > -1 ?
                    this.configService?.config?.onboardingRequestPlugins[pluginName] : null;
                this.pluginName = pluginName;
                this.modelMeta = response.modelMeta;
                this.offeringMeta = response.offeringMeta;
                this.landingPages = plugin?.landingPages.map(l => { return { name: l.landingPage, completeOnboardingPages: l.completeOnboardingPages } });
                this.sections = plugin?.sections;
                this.offeringConfigMetaPath = (response.offeringConfigMetaPath || '').replace(/\\/g, '/');
                this.pluginConfigMetaPath = (response.pluginConfigMetaPath || '').replace(/\\/g, '/');
                this.verificationPlugins = response.verificationPlugins;
                if (!response.onboardingConfig.offeringsSelectionConfigs) {
                    response.onboardingConfig.offeringsSelectionConfigs = [];
                }
                if (!response.onboardingConfig.landingPages) {
                    response.onboardingConfig.landingPages = [];
                }
                if (!response.onboardingConfig.onboardingConfigVerifications) {
                    response.onboardingConfig.onboardingConfigVerifications = [];
                }
                if (!response.onboardingConfig.onboardingRequiredDocumentTypes) {
                    response.onboardingConfig.onboardingRequiredDocumentTypes = [];
                }
                if (!response.onboardingConfig.onboardingWhatsNeededSteps) {
                    response.onboardingConfig.onboardingWhatsNeededSteps = [];
                }

                this.subscriptions.push(this.bindingEngine.propertyObserver(response.onboardingConfig, 'isGeoLocationEnabled').subscribe(() => {
                    if (!this.item.isGeoLocationEnabled && response.onboardingConfig.allowedCountries) {
                        const length = response.onboardingConfig.allowedCountries.length;
                        for (let index = length - 1; index >= 0; index--) {
                            response.onboardingConfig.allowedCountries.splice(index, 1);
                        }
                    }
                }));
                resolve(response.onboardingConfig);
            });
        });
    }

    protected beforeSave(skipValidation: boolean, resolve: (isValid: boolean) => void) {
        this.item.offeringsSelectionConfigs.forEach(x => {
            const queries = JSON.stringify(x.queries);
            if (queries != x.queryBody) {
                x.queryBody = queries;
            }
        });
        if (this.onboardingBasicMeta.basicMetaConfig) {
            if (this.item.isBasicMetaFormStyle) {
                this.item.basicMetaConfig = JSON.stringify(this.onboardingBasicMeta.basicMetaConfig);
            } else if (typeof this.onboardingBasicMeta.basicMetaConfig == 'string') {
                this.item.basicMetaConfig = this.onboardingBasicMeta.basicMetaConfig;
            }
        }
        if (this.onboardingAdvancedMeta.advancedMetaConfig) {
            if (this.item.isAdvancedMetaFormStyle) {
                this.item.advancedMetaConfig = JSON.stringify(this.onboardingAdvancedMeta.advancedMetaConfig);
            } else if (typeof this.onboardingAdvancedMeta.advancedMetaConfig == 'string') {
                this.item.advancedMetaConfig = this.onboardingAdvancedMeta.advancedMetaConfig;
            }
        }
        if (this.onboardingOffering.offeringsMetaConfig) {
            this.item.offeringsMetaConfig = JSON.stringify(this.onboardingOffering.offeringsMetaConfig);
        }
        this.item.onboardingConfigVerifications.forEach(onboardingConfigVerification => {
            onboardingConfigVerification.meta = JSON.stringify(onboardingConfigVerification.metaInstance);
        });
        resolve(true);
    }

    protected initializeItemForCreate(): Promise<OnboardingConfigModel> {
        return this.getItem(0);
    }

    private initializeItem(item: OnboardingConfigModel) {
        if (item.onboardingWhatsNeededSteps.filter(x => !x.isForExistingCustomer).length == 0) {
            item.onboardingWhatsNeededSteps.push(
                {
                    id: 0,
                    onboardingWhatsNeededStepId: 0,
                    onboardingConfigId: item.onboardingConfigId,
                    icon: 'person',
                    title: '',
                    description: '',
                    isForExistingCustomer: false
                });
        }
        if (item.onboardingWhatsNeededSteps.filter(x => x.isForExistingCustomer).length == 0) {
            item.onboardingWhatsNeededSteps.push(
                {
                    id: 0,
                    onboardingWhatsNeededStepId: 0,
                    onboardingConfigId: item.onboardingConfigId,
                    icon: 'person',
                    title: '',
                    description: '',
                    isForExistingCustomer: true
                });
        }
    }

    protected initializeItemOverride(item: OnboardingConfigModel) {
        item.isBasicMetaFormStyle = true;
        item.isAdvancedMetaFormStyle = true;
        this.item.basicMetaConfig = JSON.stringify({ pages: [] });
        this.item.advancedMetaConfig = JSON.stringify({ pages: [] });
        this.initializeItem(item);
    }

    protected initializeItemOverrideForUpdate(item: OnboardingConfigModel) {
        this.initializeItem(item);
    }

    protected saveItem(id: number, changes: { [index: string]: UnitOfWorkItem }): Promise<BaseValidationResponse> {
        return new Promise<BaseValidationResponse>((resolve, reject) => {
            const request: any = {
                id: id,
                changes: changes,
                onboardingRequiredDocumentTypeFiles: Object.keys(this.onboardingPluginConfig.onboardingRequiredDocumentTypeFiles).map(key =>
                ({
                    guid: key,
                    file: this.onboardingPluginConfig.onboardingRequiredDocumentTypeFiles[key]
                }))
            };
            this.onboardingAdminService.saveOnboardingConfig(request).then(resolve).catch(reject);
        });
    }

    protected getUpdateRoute(): string {
        return GenesisRouterConstants.RouteNames.PersistOnboarding;
    }

    protected getEntityType(): EntityTypes {
        return EntityTypes.OnboardingConfig;
    }
}
